/**
 * KRIMA Combo Cart Guard — assets/krima-combo-cart-guard.js
 * Versión: 5.2.1
 *
 * Protege el fee item (cargo de sobrecosto) de un combo: evita que el cliente
 * lo elimine o modifique directamente, y limpia los fee items huérfanos que
 * quedan cuando se elimina el combo que los originó.
 *
 * Detección de huérfanos en tres capas:
 *   A) Interceptor de window.fetch
 *   B) Interceptor de XMLHttpRequest (el tema puede usar XHR)
 *   C) Polling cada 500ms cuando hay fee items en el carrito
 *
 * Cada fee item se vincula a su combo vía la property "_combo_parent_key"
 * (agregada por krima-combo-v2.js al crearlo). Fee items sin ese vínculo
 * (de antes de este mecanismo) caen a un fallback: huérfano solo si no queda
 * ningún combo en el carrito — ver computeOrphanFeeItems().
 *
 * INSTALACIÓN: antes de </body> en theme.liquid:
 *   <script src="{{ 'krima-combo-cart-guard.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  const FEE_PROP        = '_combo_fee';
  const PARENT_KEY_PROP = '_combo_parent_key';
  const GUARD_ATTR      = 'data-krima-fee-locked';

  // ─── Helpers ──────────────────────────────────────────────────────────────────

  function isFeeItem(item) {
    return item.properties?.[FEE_PROP] === 'true';
  }

  function isComboItem(item) {
    return !isFeeItem(item) &&
      Object.keys(item.properties || {}).some(k => k.startsWith('Selección'));
  }

  async function getCart() {
    return window._krimaOriginalFetch('/cart.js').then(r => r.json());
  }

  // ─── Estado global ────────────────────────────────────────────────────────────

  const state = {
    feeLineKeys:    new Set(),
    internalUpdate: false,
  };

  // Guardar referencias originales ANTES de parchear
  window._krimaOriginalFetch = window.fetch;
  const OriginalXHR = window.XMLHttpRequest;

  // ─── Sincronización de keys ───────────────────────────────────────────────────

  async function syncKeys() {
    try {
      const cart = await getCart();
      state.feeLineKeys.clear();
      cart.items.forEach(item => {
        if (isFeeItem(item)) state.feeLineKeys.add(item.key);
      });
    } catch (e) {}
  }

  // ─── Limpieza de huérfanos ───────────────────────────────────────────────────

  let orphanCheckPending = false;

  // Un fee item es huérfano si el combo que lo originó (_combo_parent_key) ya
  // no está en el carrito. Si no tiene ese vínculo (fee legacy), se usa el
  // fallback anterior: huérfano solo si no queda ningún combo.
  function computeOrphanFeeItems(cart) {
    const feeItems = cart.items.filter(isFeeItem);
    if (!feeItems.length) return [];

    const comboKeys = new Set(cart.items.filter(isComboItem).map(i => i.key));

    return feeItems.filter(fee => {
      const parentKey = fee.properties?.[PARENT_KEY_PROP];
      if (parentKey) return !comboKeys.has(parentKey);
      return comboKeys.size === 0;
    });
  }

  async function removeFeeItems(feeItems) {
    const updates = {};
    feeItems.forEach(item => { updates[item.key] = 0; });

    state.internalUpdate = true;
    const r = await window._krimaOriginalFetch('/cart/update.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ updates }),
    });
    state.internalUpdate = false;
    return r;
  }

  // Quita del DOM el <li> del fee item ya eliminado en el backend (sin reload)
  // y dispara 'cart:refresh'/'cart:updated' por si el tema los escucha para
  // resincronizar subtotal/contador. findEl() debe correr con el "cart" que
  // todavía incluye el item, antes de eliminarlo.
  function removeFeeItemsVisually(feeItems, cart) {
    feeItems.forEach(item => {
      state.feeLineKeys.delete(item.key);
      const el = findEl(item, cart);
      if (!el) return;
      el.style.transition = 'opacity 150ms ease, transform 150ms ease';
      el.style.opacity   = '0';
      el.style.transform = 'scale(0.98)';
      setTimeout(() => el.remove(), 160);
    });

    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'krima-cart-guard' } }));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'krima-cart-guard' } }));
  }

  async function checkOrphans() {
    if (orphanCheckPending) return;
    orphanCheckPending = true;

    try {
      const cart           = await getCart();
      const orphanFeeItems = computeOrphanFeeItems(cart);
      if (!orphanFeeItems.length) { orphanCheckPending = false; return; }

      console.log('[KrimaCartGuard] Fee items huérfanos detectados — eliminando...', orphanFeeItems.map(i => i.key));

      const r = await removeFeeItems(orphanFeeItems);

      if (r.ok) {
        console.log('[KrimaCartGuard] Fee items huérfanos eliminados correctamente.');
        removeFeeItemsVisually(orphanFeeItems, cart);
      } else {
        console.warn('[KrimaCartGuard] No se pudieron eliminar los fee items huérfanos:', r.status);
      }
    } catch (e) {
      state.internalUpdate = false;
    }

    orphanCheckPending = false;
  }

  function scheduleOrphanCheck() {
    setTimeout(checkOrphans, 700);
  }

  // ─── A) Interceptor de fetch ──────────────────────────────────────────────────

  window.fetch = function (url, options = {}) {
    const urlStr = typeof url === 'string' ? url : (url?.url || '');

    if (!state.internalUpdate) {

      // Bloquear eliminación/modificación del fee item
      if (urlStr.includes('/cart/change') && isFeeTarget(options)) {
        console.warn('[KrimaCartGuard] Eliminación del cargo adicional bloqueada (fetch).');
        return window._krimaOriginalFetch('/cart.js');
      }

      if (urlStr.includes('/cart/update')) {
        const s = sanitizeUpdate(options);
        if (s.blocked) {
          console.warn('[KrimaCartGuard] Modificación del cargo adicional bloqueada (fetch).');
          return s.hasOther
            ? window._krimaOriginalFetch(url, { ...options, body: JSON.stringify(s.body) })
            : window._krimaOriginalFetch('/cart.js');
        }
      }

      // Detectar cambios en el carrito para buscar huérfanos
      if (urlStr.includes('/cart/change') || urlStr.includes('/cart/update')) {
        const promise = window._krimaOriginalFetch.call(this, url, options);
        promise.then(res => { if (res.ok) scheduleOrphanCheck(); }).catch(() => {});
        return promise;
      }
    }

    return window._krimaOriginalFetch.call(this, url, options);
  };

  // ─── B) Interceptor de XMLHttpRequest ─────────────────────────────────────────

  window.XMLHttpRequest = function () {
    const xhr     = new OriginalXHR();
    let   xhrUrl  = '';

    const originalOpen = xhr.open.bind(xhr);
    xhr.open = function (method, url, ...rest) {
      xhrUrl = String(url || '');
      return originalOpen(method, url, ...rest);
    };

    const originalSend = xhr.send.bind(xhr);
    xhr.send = function (body) {
      const isCartChange = xhrUrl.includes('/cart/change') || xhrUrl.includes('/cart/update');

      if (isCartChange && !state.internalUpdate) {
        // Verificar si apunta a un fee item (body como string o FormData)
        if (xhrUrl.includes('/cart/change') && isFeeTargetFromBody(body)) {
          console.warn('[KrimaCartGuard] Eliminación del cargo adicional bloqueada (XHR).');
          // No enviar — devolver respuesta vacía válida del carrito
          window._krimaOriginalFetch('/cart.js')
            .then(r => r.text())
            .then(text => {
              Object.defineProperty(xhr, 'responseText', { value: text, writable: true });
              Object.defineProperty(xhr, 'status',       { value: 200, writable: true });
              xhr.dispatchEvent(new Event('load'));
            });
          return;
        }

        // Escuchar respuesta para detectar huérfanos
        xhr.addEventListener('load', function () {
          if (xhr.status >= 200 && xhr.status < 300) scheduleOrphanCheck();
        });
      }

      return originalSend(body);
    };

    return xhr;
  };

  // Heredar prototype para compatibilidad
  window.XMLHttpRequest.prototype = OriginalXHR.prototype;
  Object.keys(OriginalXHR).forEach(k => {
    try { window.XMLHttpRequest[k] = OriginalXHR[k]; } catch (e) {}
  });

  // ─── C) Polling de seguridad ──────────────────────────────────────────────────
  // Red de seguridad principal: el tema puede eliminar el combo por una vía
  // que no pasa por los interceptores de fetch/XHR. 500ms para no dar tiempo
  // a que el cliente avance al checkout antes de limpiar el huérfano.

  setInterval(async () => {
    if (state.internalUpdate) return;
    if (!state.feeLineKeys.size) return;
    await checkOrphans();
  }, 500);

  // ─── Helpers de detección ────────────────────────────────────────────────────

  function isFeeTarget(options) {
    return isFeeTargetFromBody(options?.body);
  }

  function isFeeTargetFromBody(body) {
    try {
      let id = '';
      if (body instanceof FormData) {
        id = body.get('id') || body.get('line_item_key') || '';
      } else if (typeof body === 'string') {
        if (body.trim().startsWith('{')) {
          const b = JSON.parse(body);
          id = b.id || b.line_item_key || b.key || '';
        } else {
          const p = new URLSearchParams(body);
          id = p.get('id') || p.get('line_item_key') || '';
        }
      }
      return state.feeLineKeys.has(id);
    } catch (e) { return false; }
  }

  function sanitizeUpdate(options) {
    try {
      const body = JSON.parse(options?.body || '{}');
      if (!body.updates) return { blocked: false };
      let blocked = false, hasOther = false;
      const clean = {};
      Object.entries(body.updates).forEach(([k, v]) => {
        if (state.feeLineKeys.has(k)) { blocked = true; }
        else { clean[k] = v; hasOther = true; }
      });
      return blocked ? { blocked, hasOther, body: { ...body, updates: clean } } : { blocked: false };
    } catch (e) { return { blocked: false }; }
  }

  // ─── UI: ocultar controles del fee item ───────────────────────────────────────

  function initUI() {
    runUI();
    new MutationObserver(() => runUI())
      .observe(document.body, { childList: true, subtree: true });
    ['cart:refresh', 'cart:updated', 'cart:open'].forEach(evt =>
      document.addEventListener(evt, () => setTimeout(runUI, 200))
    );
  }

  async function runUI() {
    try {
      const cart     = await getCart();
      const feeItems = cart.items.filter(isFeeItem);
      feeItems.forEach(item => {
        const el = findEl(item, cart);
        if (el && !el.hasAttribute(GUARD_ATTR)) lockEl(el);
      });
    } catch (e) {}
  }

  function findEl(item, cart) {
    const key = item.key;
    const idx = cart.items.indexOf(item) + 1;

    // Todo selector acá debe depender del key real del item, no de su
    // posición — un selector solo por índice puede apuntar al elemento
    // equivocado justo después de que el carrito cambie.
    for (const sel of [
      `[data-line-item-key="${key}"]`,
      `#CartItem-${idx}-${key}`,
    ]) {
      const el = document.querySelector(sel);
      if (el) return el.closest('li') || el;
    }

    for (const li of document.querySelectorAll('li[x-data]')) {
      if (li.getAttribute('x-data')?.includes(key)) return li;
    }

    return null;
  }

  function lockEl(el) {
    el.setAttribute(GUARD_ATTR, 'true');

    el.querySelectorAll('a[href*="quantity=0"], a[href*="/cart/change"]')
      .forEach(a => { (a.closest('p') || a).style.cssText = 'display:none!important'; });

    const qBlock = el.querySelector('[x-data="CartItemQuantity"]');
    if (qBlock) {
      qBlock.querySelectorAll('button[aria-label="−"], button[aria-label="+"]')
        .forEach(btn => {
          btn.disabled = true;
          btn.style.cssText = 'pointer-events:none!important;opacity:0.35!important;cursor:not-allowed!important';
        });

      const input = qBlock.querySelector('input[name="updates[]"], input[type="number"]');
      if (input) {
        input.readOnly = true;
        input.style.cssText = 'pointer-events:none!important;opacity:0.6!important';
        input.addEventListener('change', e => e.stopImmediatePropagation(), { capture: true });
        input.addEventListener('keyup',  e => e.stopImmediatePropagation(), { capture: true });
      }

      qBlock.addEventListener('remove', e => {
        e.stopImmediatePropagation();
        e.preventDefault();
      }, { capture: true });
    }

    console.log('[KrimaCartGuard] Fee item bloqueado visualmente.');
  }

  // ─── Bootstrap ───────────────────────────────────────────────────────────────

  async function init() {
    // Una sola llamada a /cart.js para sincronizar keys y revisar huérfanos,
    // minimizando la ventana en la que un reload del tema podría interrumpir
    // la limpieza.
    try {
      const cart = await getCart();

      state.feeLineKeys.clear();
      cart.items.filter(isFeeItem).forEach(item => state.feeLineKeys.add(item.key));

      const orphanFeeItems = computeOrphanFeeItems(cart);
      if (orphanFeeItems.length) {
        console.log('[KrimaCartGuard] Fee items huérfanos detectados — eliminando...', orphanFeeItems.map(i => i.key));

        const r = await removeFeeItems(orphanFeeItems);

        if (r.ok) {
          console.log('[KrimaCartGuard] Fee items huérfanos eliminados correctamente.');
          removeFeeItemsVisually(orphanFeeItems, cart);
        } else {
          console.warn('[KrimaCartGuard] No se pudieron eliminar los fee items huérfanos:', r.status);
        }
      }
    } catch (e) {
      state.internalUpdate = false;
    }

    initUI();
    console.log('[KrimaCartGuard] v5.2 activo. Fee keys:', [...state.feeLineKeys]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.KrimaCartGuard = {
    state, syncKeys, checkOrphans, runUI,
    cleanOrphans: async () => {
      state.internalUpdate = true;
      const cart    = await getCart();
      const updates = {};
      cart.items.filter(isFeeItem).forEach(i => { updates[i.key] = 0; });
      if (!Object.keys(updates).length) {
        state.internalUpdate = false;
        console.log('[KrimaCartGuard] No hay fee items huérfanos.');
        return;
      }
      await window._krimaOriginalFetch('/cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      state.internalUpdate = false;
      location.reload();
    },
  };

})();
